import { useEffect } from 'react';
import { useFormik } from 'formik';
import { Col, Row, Typography } from 'antd';
import * as Yup from 'yup';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import { DefaultOptionType } from 'antd/lib/select';
import { StyledIconWrapper } from 'components/new-ui/base/wrappers/styles';

type Props = {
    open: boolean;
    loading?: boolean;
    options: DefaultOptionType[];
    onCancel: () => void;
    onConfirm: (jobId: string) => void;
    onSearch?: (q: string) => void;
};

const AddJobModal = ({ open, loading, options, onCancel, onConfirm, onSearch }: Props) => {
    const formik = useFormik({
        initialValues: { jobId: '' },
        validationSchema: Yup.object({ jobId: Yup.string().trim().required().nullable() }),
        validateOnMount: true,
        onSubmit: (v) => onConfirm(v.jobId),
    });

    const handleSelectOption = (_: string, option: any) => {
        formik.setFieldValue('jobId', option.id);
    };

    const handleClear = () => formik.setFieldValue('jobId', null);

    useEffect(() => {
        if (!open) formik.resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <ConfirmationModal
            title="Add Job"
            confirmButtonText="Add"
            open={open}
            confirmationButtonDisabled={!formik.isValid}
            confirmationButtonLoading={loading || formik.isSubmitting}
            onCancel={() => {
                onCancel();
                formik.resetForm();
            }}
            onConfirm={formik.submitForm}
            icon={
                <StyledIconWrapper>
                    <SuitCaseIcon />
                </StyledIconWrapper>
            }
            content={
                <form style={{ width: '100%' }} onSubmit={formik.handleSubmit}>
                    <Typography.Text>
                        Please select a job that you want to add to the manifest. Only draft jobs can be added to the
                        manifest.
                    </Typography.Text>
                    <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
                        <Col md={8} xs={24}>
                            <label>Job ID:</label>
                        </Col>
                        <Col md={16} xs={24}>
                            <FormAutocomplete
                                onBlur={() => formik.setFieldTouched('jobId')}
                                onClear={handleClear}
                                fieldMeta={formik.getFieldMeta('jobId')}
                                fieldName="jobId"
                                style={{ width: '100%' }}
                                inputPlaceholder="Search jobs"
                                onSearch={onSearch}
                                options={options}
                                onSelect={handleSelectOption}
                                value={
                                    formik.values.jobId
                                        ? options.find((o) => (o as any).id === formik.values.jobId)?.value
                                        : undefined
                                }
                            />
                        </Col>
                    </Row>
                </form>
            }
        />
    );
};

export default AddJobModal;
